import { Trans, useTranslation } from '@sortlist-frontend/translation/ssr';
import { createRef, ForwardedRef, Fragment, ReactNode, useRef } from 'react';

import { ExpandablePanels, QuestionAndAnswer } from '_components/common/ExpandablePanels';
import { usePublicAppContext } from '_core/context/public-app-context';
import { legalConfig } from '_features/legal/legal.config';
import * as S from '_features/legal/legal.styles';
import { LegalSection } from '_features/legal/legal.types';

import { AsideBlock, SectionRef } from './components/AsideBlock';

type LegalProps = {
  lang: string;
  origin: string;
  pageTitle: string | ReactNode;
  contentTitle: string | ReactNode;
  pageContent: LegalSection[];
  pageDate: Date;
  scrollableCtn?: ForwardedRef<HTMLDivElement>;
  QAList?: QuestionAndAnswer[];
};

export default function LegalPage(props: LegalProps) {
  const { scrollableCtn, pageTitle, contentTitle, pageContent, pageDate, QAList } = props;
  const { t } = useTranslation(legalConfig.i18nNamespaces);
  const { locale } = usePublicAppContext();

  const dateOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  } as const;

  const sectionRefs = useRef<SectionRef[]>(
    pageContent.map((section: LegalSection, index: number) => {
      return {
        ref: createRef<HTMLDivElement>(),
        subRefs: [...new Array(pageContent[index]?.subsections?.length)].map(() => createRef<HTMLDivElement>()),
      };
    }),
  );

  return (
    <Fragment>
      <S.Header className="pt-32 pb-16 bg-secondary-50">
        <div className="container-lg text-left">
          <S.Title className="text-break-word serif bold mb-2">{pageTitle}</S.Title>
          <p className="text-secondary-500 text-break-word">
            <Trans i18nKey={'legal:update'}>
              Last update: {{ date: pageDate?.toLocaleDateString(locale, dateOptions) }}
            </Trans>
          </p>
        </div>
      </S.Header>
      <div>
        <div className="container-lg p-0 layout-row">
          <section className="pt-16 pb-8 px-4 flex-1">
            <h2 className="bold">{contentTitle}</h2>
            {pageContent.map((section: LegalSection, index: number) => {
              const sectionIndex = index;
              return (
                <S.Section
                  className="my-8"
                  key={`section-${section.id}`}
                  id={section.id}
                  ref={sectionRefs?.current[sectionIndex].ref}>
                  <h3 className="bold mb-4" dangerouslySetInnerHTML={{ __html: section.title as string }} />
                  <div
                    className="text-secondary-700 lh-2"
                    dangerouslySetInnerHTML={{ __html: section.content as string }}
                  />
                  {section.subsections &&
                    section.subsections?.map((subsection: LegalSection, index: number) => (
                      <div
                        key={`subsection-${subsection.id}`}
                        id={subsection.id}
                        ref={sectionRefs.current[sectionIndex].subRefs[index]}>
                        <div
                          className="bold h5 mb-4"
                          dangerouslySetInnerHTML={{ __html: subsection.title as string }}
                        />
                        <div
                          className="text-secondary-700 lh-3"
                          dangerouslySetInnerHTML={{ __html: subsection.content as string }}
                        />
                      </div>
                    ))}
                </S.Section>
              );
            })}
          </section>
          <section className="layout-column pt-16 pb-8 px-4 flex-33 hide show-gt-sm ">
            <AsideBlock pageContent={pageContent} sectionRefs={sectionRefs} scrollableCtn={scrollableCtn} />
          </section>
        </div>
        <div>
          {QAList && (
            <div className="py-8 container-md p-0 layout-align-center-center">
              <h3 className="bold text-center pb-8">{t('legal:refundPolicy.faq.title')}</h3>
              <ExpandablePanels className="rounded-lg shadow-2 border border-secondary-700" QAList={QAList} />
            </div>
          )}
        </div>
      </div>
    </Fragment>
  );
}
